import {
	ArrayMaxSize,
	ArrayMinSize,
	ArrayNotEmpty,
	ArrayUnique,
	IsArray, IsEmail,
	IsHexColor,
	IsNotEmpty,
	IsObject,
	IsOptional,
	IsString,
	Matches,
	MinLength,
	ValidateIf,
	ValidateNested
} from 'class-validator';
import { Type } from 'class-transformer';
import {
	AccountsPrivacyOptionsEnum,
	AddInterestToUserDto,
	ALLOWED_SWEAT_MATCH_IMAGE_MIME_TYPES,
	ALLOWED_SWEAT_MATCH_VIDEO_MIME_TYPES,
	ALLOWED_USER_AVATAR_MIME_TYPES,
	ALLOWED_USER_BG_IMAGE_MIME_TYPES,
	Base64AllowedMimeTypes,
	FileUploadDto,
	LocationPrivacyOptionsEnum,
	MaxBase64FileSize,
	NO_WHITESPACE_REGEXP,
	UpdateUserDto,
} from '@sweatpals/shared';
import { PlacesAutocompleteOption } from '../../interfaces';

export interface CreateCommunityAdditionalFormData {
	addresses?: PlacesAutocompleteOption[];
	avatarId?: string | null;
	backgroundId?: string | null;
}

export class CreateCommunityFormValidator extends UpdateUserDto implements CreateCommunityAdditionalFormData {
	@IsString()
	@IsNotEmpty()
	@MinLength(3, { message: 'Full name is too short' })
	public override fullName?: string;

	@IsString()
	@IsNotEmpty()
	@MinLength(3, { message: 'User name is too short' })
	@Matches(NO_WHITESPACE_REGEXP, { message: 'Nickname should contain only letters and digits' })
	public override userName?: string;

	@IsOptional()
	@IsEmail(undefined, { message: 'Email is not valid' })
	public override receiptsEmail?: string;

	@ValidateIf((o) => !o.addresses?.length)
	@IsString()
	@IsNotEmpty()
	@MinLength(3, { message: 'Address is too short' })
	public override homeAddressName?: string;

	@ValidateIf((o) => !o.addresses?.length)
	@IsOptional()
	@IsString()
	@IsNotEmpty()
	public override homeAddressLat?: string;

	@ValidateIf((o) => !o.addresses?.length)
	@IsOptional()
	@IsString()
	@IsNotEmpty()
	public override homeAddressLng?: string;

	@IsNotEmpty()
	@ArrayNotEmpty({ message: 'Select at least one location' })
	@ArrayUnique((d) => d.description)
	public addresses?: PlacesAutocompleteOption[];

	@IsOptional()
	@IsString()
	@IsNotEmpty()
	public override locationPrivacyOptionId?: LocationPrivacyOptionsEnum;

	@IsOptional()
	@IsArray()
	@ArrayNotEmpty({ message: 'Select at least one interest' })
	public override interestsToInsert?: AddInterestToUserDto[];

	@IsString()
	@IsNotEmpty()
	@IsHexColor()
	public override primaryColor?: string | null;

	@IsNotEmpty({ message: 'Privacy option should not be empty' })
	public override accountPrivacyOptionId?: AccountsPrivacyOptionsEnum;

	@IsOptional()
	@IsObject()
	@IsNotEmpty()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_AVATAR_MIME_TYPES)
	public override updatedAvatarImg?: FileUploadDto | null;

	@IsOptional()
	@ValidateNested()
	@Type(() => FileUploadDto)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_USER_BG_IMAGE_MIME_TYPES)
	public override updatedBackgroundImg?: FileUploadDto | null;

	@IsOptional()
	@IsArray()
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_IMAGE_MIME_TYPES)
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	public override imagesToUpload?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_SWEAT_MATCH_VIDEO_MIME_TYPES)
	public override videosToUpload?: FileUploadDto[];

	public avatarId?: string | null;
	public backgroundId?: string | null;
}

export class EditCommunityFormValidator extends CreateCommunityFormValidator {}
